import * as XLSX from 'xlsx';
import { formatDateShort, isValidDateValue } from './dateFormat';

/**
 * Check if a value is a valid date
 */
const isValidDate = (value: any): boolean => {
  return isValidDateValue(value);
};

/**
 * Downloads data as CSV file
 */
export const downloadCSV = (data: any[], filename: string) => {
  const csvContent = convertToCSV(data);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Downloads data as XLSX file
 */
export const downloadXLSX = (data: any[], filename: string, sheetName: string = 'Data') => {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
};

/**
 * Converts array of objects to CSV string
 */
const convertToCSV = (data: any[]): string => {
  if (!data || data.length === 0) return '';
  
  const headers = Object.keys(data[0]);
  const csvHeaders = headers.join(',');
  
  const csvRows = data.map(row => 
    headers.map(header => {
      const value = row[header];
      // Handle values that contain commas, quotes, or newlines
      if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    }).join(',')
  );
  
  return [csvHeaders, ...csvRows].join('\n');
};

/**
 * Generate filename with timestamp
 */
export const generateFilename = (baseName: string, extension: string): string => {
  const timestamp = new Date().toISOString().split('T')[0];
  return `${baseName}_${timestamp}.${extension}`;
};

/**
 * Format transaction data for export
 */
export const formatTransactionForExport = (txn: any, selectedFields: string[], workflowFields: string[] = [], teamFieldOptions: any[] = []) => {
  const row: any = {};
  
  // Process basic fields
  selectedFields.forEach(fieldKey => {
    switch (fieldKey) {
      case 'transactionId':
        row['Transaction ID'] = txn.key || 'N/A';
        break;
      case 'clientName':
        row['Client Name'] = txn.client?.name || txn.team?.client?.name || 'N/A';
        break;
      case 'teamName':
        row['Process Name'] = txn.team?.name || 'N/A';
        break;
      case 'createdDate':
        row['Created Date'] = txn.createdAt || txn.created_at || txn.createdDate
          ? formatDateShort(txn.createdAt || txn.created_at || txn.createdDate)
          : 'N/A';
        break;
      case 'updatedDate':
        row['Updated Date'] = txn.updatedAt || txn.updated_at || txn.updatedDate || txn.lastModified
          ? formatDateShort(txn.updatedAt || txn.updated_at || txn.updatedDate || txn.lastModified)
          : 'N/A';
        break;
      case 'status':
        row['Current Status'] = txn.displayStatus || 'N/A';
        break;
    }
  });
  
  // Process workflow fields
  if (workflowFields && workflowFields.length > 0) {
    workflowFields.forEach(fieldId => {
      // Find the field definition in teamFieldOptions to get the label
      let fieldLabel = fieldId; // fallback to field ID
      let fieldValue = 'N/A';
      
      // Search through team field options to find the field definition
      for (const step of teamFieldOptions) {
        const field = step.component?.fields?.find((f: any) => 
          (f.uid || f._id || f.id) === fieldId
        );
        if (field) {
          fieldLabel = field.question || field.label || field.name || fieldId;
          break;
        }
      }
      
      // First, try to get value from TSN history
      if (txn.history && Array.isArray(txn.history)) {
        for (const historyEntry of txn.history) {
          if (historyEntry.data && Array.isArray(historyEntry.data)) {
            const fieldData = historyEntry.data.find((dataItem: any) => 
              (dataItem.field === fieldId || dataItem.uid === fieldId) || 
              (dataItem.question === fieldLabel)
            );
            
            if (fieldData && fieldData.value) {
              // Check if it has files
              if (fieldData.value.files && Array.isArray(fieldData.value.files) && fieldData.value.files.length > 0) {
                // Extract file URLs
                const fileUrls = fieldData.value.files
                  .filter((file: any) => file.url)
                  .map((file: any) => file.url);
                fieldValue = fileUrls.length > 0 ? fileUrls.join(', ') : 'N/A';
              } else if (fieldData.value.value) {
                // Extract regular values
                const value = fieldData.value.value;
                if (Array.isArray(value)) {
                  // Check if array contains resources or dates and format them
                  const formattedValues = value.map(v => {
                    // Check if it's a resource with fields
                    if (v && typeof v === 'object' && v.fields) {
                      // Extract Name field or format the entire fields object
                      return v.fields.Name || v.fields.name || formatFieldsObject(v.fields);
                    } else if (isValidDate(v)) {
                      return formatDateShort(v);
                    }
                    return v;
                  });
                  fieldValue = formattedValues.join(' || ');
                } else if (typeof value === 'string') {
                  // Check if string is a date and format it
                  if (isValidDate(value)) {
                    fieldValue = formatDateShort(value);
                  } else {
                    fieldValue = value;
                  }
                } else if (value && typeof value === 'object' && value.fields) {
                  // Handle single resource object with fields
                  fieldValue = value.fields.Name || value.fields.name || formatFieldsObject(value.fields);
                } else {
                  fieldValue = String(value);
                }
              }
              break; // Found the field, stop searching
            }
          }
        }
      }
      
      // If not found in TSN history, check additionalFields as fallback
      if (fieldValue === 'N/A' && txn.additionalFields && typeof txn.additionalFields === 'object') {
        const value = txn.additionalFields[fieldId] || txn.additionalFields[fieldLabel];
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            // Check if array contains resources or dates and format them
            const formattedValues = value.map(v => {
              // Check if it's a resource with fields
              if (v && typeof v === 'object' && v.fields) {
                return v.fields.Name || v.fields.name || formatFieldsObject(v.fields);
              } else if (isValidDate(v)) {
                return formatDateShort(v);
              }
              return v;
            });
            fieldValue = formattedValues.join(' || ');
          } else if (typeof value === 'object') {
            // Check if it's a resource with fields
            if (value.fields) {
              fieldValue = value.fields.Name || value.fields.name || formatFieldsObject(value.fields);
            } else {
              fieldValue = JSON.stringify(value);
            }
          } else {
            // Check if it's a date and format it
            if (isValidDate(value)) {
              fieldValue = formatDateShort(value);
            } else {
              fieldValue = String(value);
            }
          }
        }
      }
      
      // Also check if the field exists directly on the transaction object as final fallback
      if (fieldValue === 'N/A' && txn[fieldId] !== null && txn[fieldId] !== undefined) {
        if (Array.isArray(txn[fieldId])) {
          // Check if array contains resources or dates and format them
          const formattedValues = txn[fieldId].map((v: any) => {
            // Check if it's a resource with fields
            if (v && typeof v === 'object' && v.fields) {
              return v.fields.Name || v.fields.name || formatFieldsObject(v.fields);
            } else if (isValidDate(v)) {
              return formatDateShort(v);
            }
            return v;
          });
          fieldValue = formattedValues.join(' || ');
        } else if (typeof txn[fieldId] === 'object') {
          // Check if it's a resource with fields
          if (txn[fieldId].fields) {
            fieldValue = txn[fieldId].fields.Name || txn[fieldId].fields.name || formatFieldsObject(txn[fieldId].fields);
          } else {
            fieldValue = JSON.stringify(txn[fieldId]);
          }
        } else {
          // Check if it's a date and format it
          if (isValidDate(txn[fieldId])) {
            fieldValue = formatDateShort(txn[fieldId]);
          } else {
            fieldValue = String(txn[fieldId]);
          }
        }
      }
      
      row[fieldLabel] = fieldValue;
    });
  }
  
  return row;
};

/**
 * Format fields object into a structured readable format
 */
const formatFieldsObject = (fields: any): string => {
  if (!fields || typeof fields !== 'object') {
    return String(fields);
  }
  
  // If it's an array of objects with key-value pairs
  if (Array.isArray(fields)) {
    return fields.map(item => {
      if (typeof item === 'object' && item !== null) {
        return Object.entries(item)
          .map(([key, value]) => `${key}: ${value}`)
          .join(', ');
      }
      return String(item);
    }).join(' || ');
  }
  
  // If it's a single object with key-value pairs
  return Object.entries(fields)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
};
